import { forwardRef, useRef, useState } from 'react';

import { IconButton } from '../Button';
import { InfoIcon } from '../Icon';
import { Tooltip } from '../Tooltip';
import { cn } from '../utils';
import { infoButtonRecipe } from './InfoButton.recipe';

import type { InfoButtonProps } from './types';

/**
 * Design System 2.0 info button — a 16px trigger that opens a Tooltip with optional extra context.
 *
 * Ported from the `Info Button` set (file 5aSkE0U4TeWbuIZDvccHjc, node 1945:115418). The set is a
 * composition, and so is this: a real `IconButton` (`3xs`, Muted) carrying the `Info` glyph, inside
 * the DS `Tooltip` at its `Top` cell. Nothing here restyles either one except the resting glyph
 * colour — see `infoButtonRecipe`.
 *
 * State maps onto the browser like this:
 *
 *   Default   resting
 *   Hover     pointer over — Tooltip opens after its hover delay; Button draws the hover tint
 *   Focus     keyboard focus — Tooltip opens at once; Button draws the shared focus ring
 *   Active    a tap or click — toggles the tooltip, which is what makes it reachable on touch
 *
 * The toggle is why `closeOnClick` is off. Base UI would otherwise close on pointerdown and this
 * click would reopen it, so a second tap could never close it. A tap outside and Escape still
 * close it, through Base UI's own dismiss handling.
 *
 * The toggle reads the state at POINTERDOWN, not at click. A press that also focuses the button
 * with `:focus-visible` set opens the tooltip on focus between the two events, and toggling at
 * click would then shut it again. Keyboard activation has no pointerdown and toggles from the
 * current state, even if a cancelled press left a snapshot behind.
 *
 * Fields do not render this directly — they take `info` and `infoLabel` and place it through
 * `FieldLabel`. Use it standalone only beside a heading or label that is not a field.
 */
export const InfoButton = forwardRef<HTMLButtonElement, InfoButtonProps>(
  ({ label, 'aria-label': ariaLabel, disabled, side, align, className, 'data-testid': testId }, ref) => {
    const [open, setOpen] = useState(false);
    const openAtPress = useRef<boolean | null>(null);

    // Closed for good, not just hidden: a field disabled mid-submit would otherwise reopen its
    // tooltip on re-enable with nothing from the user.
    if (disabled && open) setOpen(false);

    return (
      <Tooltip
        align={align}
        closeOnClick={false}
        label={label}
        open={open && !disabled}
        side={side}
        onOpenChange={setOpen}
      >
        {/* `type="button"`: a field's info button almost always sits inside a form. */}
        <IconButton
          ref={ref}
          appearance="tertiary"
          aria-label={ariaLabel}
          className={cn(infoButtonRecipe(), className)}
          data-testid={testId}
          disabled={disabled}
          fill="default"
          size="3xs"
          type="button"
          onClick={(event) => {
            // `detail` is 0 for a keyboard activation, which never had a pointerdown of its own —
            // so a snapshot left behind by a cancelled press must not decide it.
            const wasOpen = event.detail > 0 ? (openAtPress.current ?? open) : open;
            openAtPress.current = null;
            setOpen(!wasOpen);
          }}
          onPointerDown={() => {
            openAtPress.current = open;
          }}
        >
          <InfoIcon />
        </IconButton>
      </Tooltip>
    );
  }
);
